import { createPlugin } from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';

// Import components (not field extensions)
import { IamPolicyMultiSelect } from './api/components/IamPolicyMultiSelect';
import { JsonInputValidator } from './JsonInputValidator';
import { PlaceholderAwareJson } from './stepfunctions/components/PlaceholderAwareJson';
import { PlaceholderAwareOpenApi } from './api/components/PlaceholderAwareOpenApi';
import { LambdaIntegrationAutoDetect } from './stepfunctions/components/LambdaIntegrationAutoDetect';
import {
  MapRunStateNameAutoDetect,
  MapRunStateNamesAutoDetect,
} from './stepfunctions/components/MapRunStateNameAutoDetect';
import { StepFunctionResourcesSummary } from './stepfunctions/components/StepFunctionResourcesSummary';
import { StepFunctionAdvancedOptionsEditor } from './stepfunctions/components/StepFunctionAdvancedOptionsEditor';
import { LambdaEditor } from './api/components/LambdaEditor';
import { LambdaDefaultsEditor } from './api/components/LambdaDefaultsEditor';
import { LambdaGroupEditor } from './api/components/LambdaGroupEditor';
import { ValkyrieLambdaEditor } from './api/components/ValkyrieLambdaEditor';
import { CondorLambdaEditor } from './api/components/CondorLambdaEditor';
import { ValkyrieLambdaDefaultsEditor } from './api/components/ValkyrieLambdaDefaultsEditor';
import { CondorLambdaDefaultsEditor } from './api/components/CondorLambdaDefaultsEditor';
import { ValkyrieQueueConsumerEditor } from './api/components/ValkyrieQueueConsumerEditor';
import { ValkyrieSqsQueueEditor } from './api/components/ValkyrieSqsQueueEditor';
import { LoggedUser } from './LoggedUser';
import { ApiTypeCombobox } from './api/components/ApiTypeCombobox';

/**
 * Scaffolder Fields Plugin
 *
 * Provides custom field extensions for Backstage scaffolder templates.
 * Following Backstage best practices by creating a plugin instance and
 * exporting field extensions from plugin.ts as recommended in the docs.
 *
 * Reference: https://backstage.io/docs/features/software-templates/writing-custom-field-extensions
 */
export const scaffolderFieldsPlugin = createPlugin({
  id: 'scaffolder-fields',
});

/**
 * Field Extensions
 *
 * All field extensions are registered here using scaffolderPlugin.provide().
 * This follows Backstage best practices for separate plugins.
 */
export const IamPolicyFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'IamPolicyMultiSelect',
    component: IamPolicyMultiSelect,
  }),
);

export const JsonInputValidatorFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'JsonInputValidator',
    component: JsonInputValidator,
  }),
);

export const PlaceholderAwareJsonFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'PlaceholderAwareJson',
    component: PlaceholderAwareJson,
  }),
);

export const PlaceholderAwareOpenApiFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'PlaceholderAwareOpenApi',
    component: PlaceholderAwareOpenApi,
  }),
);

export const LambdaIntegrationAutoDetectFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'LambdaIntegrationAutoDetect',
    component: LambdaIntegrationAutoDetect,
  }),
);

export const MapRunStateNameAutoDetectFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'MapRunStateNameAutoDetect',
    component: MapRunStateNameAutoDetect,
  }),
);

export const MapRunStateNamesAutoDetectFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'MapRunStateNamesAutoDetect',
    component: MapRunStateNamesAutoDetect,
  }),
);

export const StepFunctionResourcesSummaryFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'StepFunctionResourcesSummary',
    component: StepFunctionResourcesSummary,
  }),
);

export const StepFunctionAdvancedOptionsEditorFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'StepFunctionAdvancedOptionsEditor',
    component: StepFunctionAdvancedOptionsEditor,
  }),
);

export const LambdaEditorFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'LambdaEditor',
    component: LambdaEditor,
  }),
);

export const LambdaDefaultsEditorFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'LambdaDefaultsEditor',
    component: LambdaDefaultsEditor,
  }),
);

export const LambdaGroupEditorFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'LambdaGroupEditor',
    component: LambdaGroupEditor,
  }),
);

export const ValkyrieLambdaEditorFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ValkyrieLambdaEditor',
    component: ValkyrieLambdaEditor,
  }),
);

export const ValkyrieLambdaDefaultsEditorFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ValkyrieLambdaDefaultsEditor',
    component: ValkyrieLambdaDefaultsEditor,
  }),
);

export const CondorLambdaEditorFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'CondorLambdaEditor',
    component: CondorLambdaEditor,
  }),
);

export const CondorLambdaDefaultsEditorFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'CondorLambdaDefaultsEditor',
    component: CondorLambdaDefaultsEditor,
  }),
);

export const ValkyrieQueueConsumerEditorFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ValkyrieQueueConsumerEditor',
    component: ValkyrieQueueConsumerEditor,
  }),
);

export const ValkyrieSqsQueueEditorFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ValkyrieSqsQueueEditor',
    component: ValkyrieSqsQueueEditor,
  }),
);

export const LoggedUserFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'LoggedUser',
    component: LoggedUser,
  }),
);

export const ApiTypeComboboxFieldScaffolder = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ApiTypeCombobox',
    component: ApiTypeCombobox,
  }),
);
